/**
 * Animated text transition using @react-spring/web.
 * Replaces react-text-transition for React 19 compatibility (no element.ref access).
 */

import { animated, config, useSpring, useTransition } from '@react-spring/web';
import React, { useEffect, useMemo, useRef, useState } from 'react';

export const presets = { gentle: config.gentle };

export interface TextTransitionProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: 'down' | 'up';
  inline?: boolean;
  springConfig?: (typeof config)[keyof typeof config];
  style?: React.CSSProperties;
  translateValue?: string;
  /** Optional stable key to avoid rerunning transitions on referentially-new children. */
  transitionKey?: string | number;
}

type TransitionItem = { key: string | number; node: React.ReactNode };
type TransitionStyles = { opacity: number; transform: string };

export function TextTransition({
  children,
  className = '',
  delay = 0,
  direction = 'up',
  inline = false,
  springConfig = config.default,
  style,
  translateValue = '100%',
  transitionKey,
}: TextTransitionProps) {
  const fromTransform = direction === 'down' ? `-${translateValue}` : translateValue;
  const leaveTransform = direction === 'down' ? translateValue : `-${translateValue}`;

  const useStableKey = transitionKey !== undefined;
  const transitionItems = useMemo(
    () => [{ key: useStableKey ? transitionKey : 0, node: children }] as TransitionItem[],
    [useStableKey, transitionKey, children],
  );
  const measureDep = useStableKey ? transitionKey : children;

  const [width, setWidth] = useState(0);
  const currentRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<string | number>('auto');
  const hasMeasured = width > 0 || height !== 'auto';

  const transitions = useTransition<TransitionItem, TransitionStyles>(transitionItems, {
    keys: (item) => item.key,
    enter: { opacity: 1, transform: 'translateY(0%)' },
    from: { opacity: 0, transform: `translateY(${fromTransform})` },
    leave: {
      opacity: 0,
      position: 'absolute',
      transform: `translateY(${leaveTransform})`,
    },
    config: springConfig,
    delay: hasMeasured ? delay : undefined,
    immediate: !hasMeasured,
  });

  useEffect(() => {
    const element = currentRef.current;
    if (!element) return;
    const { width: w, height: h } = element.getBoundingClientRect();
    setWidth(w);
    setHeight(h);
  }, [measureDep]);

  const widthTransition = useSpring({
    config: springConfig,
    delay: hasMeasured ? delay : undefined,
    immediate: !hasMeasured,
    to: { width },
  });

  return (
    <animated.div
      className={`text-transition ${className}`.trim()}
      style={{
        ...(inline && hasMeasured ? widthTransition : {}),
        ...style,
        display: inline ? 'inline-flex' : 'flex',
        height,
        whiteSpace: inline ? 'nowrap' : 'normal',
      }}
    >
      {transitions((styles, item) => (
        <animated.div
          ref={item.key === transitionItems[0]?.key ? currentRef : undefined}
          style={styles}
        >
          {item.node}
        </animated.div>
      ))}
    </animated.div>
  );
}
